import { useQuery, UseQueryOptions } from 'react-query';
import { QueryFeature } from '../../../config/queryClient';
import { authFetch } from '../../CommonUtils/authFetch';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { dateToISO8601 } from '../helpers/dates';
import { PerformancePeriodDTO } from '../types';

interface SeriesDTO {
  name: string;
  value: number;
  color: string;
}

interface PerformanceChartDTO {
  defaultGroup: string;
  groups: {
    name: string;
    series: SeriesDTO[];
  }[];
}

interface FetchPerformanceChartParams {
  clientId: string;
  period?: PerformancePeriodDTO;
  startDate?: string;
  endDate?: string;
  accountId: string;
  groupId: string;
}

const fetchPerformanceChart = async ({
  clientId,
  period,
  startDate,
  endDate,
  accountId,
  groupId,
}: FetchPerformanceChartParams): Promise<PerformanceChartDTO> => {
  return authFetch(
    urlWithParams(
      '/Consumer/OrionAccountsWidgets/PerformanceChart',
      {
        clientId,
        period,
        startDate,
        endDate,
        accountId: accountId || null,
        groupId: groupId || null,
        localDate: dateToISO8601(),
      },
      { skipNulls: true },
    ),
  );
};

interface UsePerformanceChartQueryParams {
  clientId: string;
  period?: PerformancePeriodDTO;
  startDate: string;
  endDate: string;
  accountId: string;
  groupId: string;
}

export const PERFORMANCE_CHART_QUERY_KEY = 'performanceChart';

export const usePerformanceChartQuery = (
  {
    clientId,
    period,
    startDate: _startDate,
    endDate: _endDate,
    accountId,
    groupId,
  }: UsePerformanceChartQueryParams,
  options?: UseQueryOptions<PerformanceChartDTO, Error>,
) => {
  const startDate = period === undefined ? _startDate : undefined;
  const endDate = period === undefined ? _endDate : undefined;

  return useQuery(
    [
      QueryFeature.AccountPerformanceReporting,
      PERFORMANCE_CHART_QUERY_KEY,
      clientId,
      period,
      startDate,
      endDate,
      accountId,
      groupId,
    ],
    () => fetchPerformanceChart({ clientId, period, startDate, endDate, accountId, groupId }),
    {
      retry: false,
      ...(period === undefined ? { cacheTime: 0 } : {}), // Do not cache custom ranges
      ...options,
    },
  );
};
